import { useQuery } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { FleetMemberWithUser } from '@breaker/shared-types';

export function useFleetMembers(fleetId: string | null) {
  return useQuery({
    queryKey: ['fleets', fleetId ?? '', 'members'] as const,
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ members: FleetMemberWithUser[] }>(`/v1/fleets/${fleetId}/members`),
    select: (data) => data.members,
    // Members rarely change — cache aggressively.
    staleTime: 60_000,
  });
}

/** Active Driver-badged members only, sorted by display name (0075). */
export function useDrivers(fleetId: string | null) {
  const query = useFleetMembers(fleetId);
  return {
    ...query,
    data: query.data
      ?.filter((m) => m.roles.includes('driver') && m.status === 'active')
      .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName)),
  };
}

/**
 * Build a Map<userId, FleetMemberWithUser> from a members list — fast O(1)
 * lookups for "what's this driver's name" rendering. Recompute via useMemo
 * at the call site if perf matters.
 */
export function indexMembers(
  members: FleetMemberWithUser[] | undefined,
): Map<string, FleetMemberWithUser> {
  const m = new Map<string, FleetMemberWithUser>();
  if (!members) return m;
  for (const member of members) m.set(member.userId, member);
  return m;
}
